import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password) => {
    // Unified login: backend accepts username OR email on the same field.
    const response = await axios.post(`${API}/auth/login`, {
      username: identifier,
      email: identifier,
      password,
    });
    const { access_token, user: userData, requires_2fa } = response.data;
    
    // If 2FA is required, return special response
    if (requires_2fa) {
      return { requires_2fa: true, user: userData };
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Login with CRM credentials (username-based)
  const loginWithCRM = async (username, password) => {
    const response = await axios.post(`${API}/auth/login/crm`, { username, password });
    const { access_token, user: userData, requires_2fa } = response.data;
    
    // If 2FA is required, return special response
    if (requires_2fa) {
      return { requires_2fa: true, user: userData };
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Login with local username (for admin)
  const loginLocal = async (username, password) => {
    const response = await axios.post(`${API}/auth/login/local`, { username, password });
    const { access_token, user: userData, requires_2fa } = response.data;
    
    // If 2FA is required, return special response
    if (requires_2fa) {
      return { requires_2fa: true, user: userData };
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verify2FA = async (email, totp_token) => {
    const password = sessionStorage.getItem('temp_password'); // Stored temporarily
    const response = await axios.post(`${API}/auth/login/2fa`, { 
      email, 
      password: password || '', 
      totp_token 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    sessionStorage.removeItem('temp_password');
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  // Sign in with a previously-registered Passkey (WebAuthn).
  // Uses navigator.credentials.get() — OS opens Face ID / Touch ID / Windows Hello UI.
  const loginWithPasskey = async () => {
    if (!window.PublicKeyCredential) {
      throw new Error('Passkeys are not supported on this device');
    }
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);

    // Use the locally-stored credential id (set during registration) if available.
    const stored = localStorage.getItem('tradepro_passkey_id');
    let allowCredentials;
    if (stored) {
      // base64url → Uint8Array
      const pad = '='.repeat((4 - (stored.length % 4)) % 4);
      const b64 = (stored + pad).replace(/-/g, '+').replace(/_/g, '/');
      const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
      allowCredentials = [{ type: 'public-key', id: bytes }];
    }

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: window.location.hostname,
        allowCredentials,
        userVerification: 'required',
        timeout: 60000,
      },
    });
    if (!assertion) throw new Error('Authentication cancelled');

    const credId = btoa(String.fromCharCode(...new Uint8Array(assertion.rawId)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    const response = await axios.post(`${API}/auth/passkey/login`, { credential_id: credId });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('tradepro_passkey_id', credId);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    // Explicit navigation to /login to guarantee redirect across all routes
    // (some routes may not re-render immediately on state change)
    if (typeof window !== 'undefined') {
      // Use a microtask so state update is committed first
      setTimeout(() => {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }, 50);
    }
  };

  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    loginWithCRM,
    loginLocal,
    loginWithPasskey,
    verify2FA,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
